import { ContentfulBrowseGroup, ContentfulCollection, ContentfulNavItem } from '@jbhi-fi/apis/src/contentful/types';
import { UseProductsProps } from '../../hooks/useProducts/useProducts';
import { ThumbnailImageProps } from '../../ThumbnailImage/ThumbnailImage';
import { CollectionThumbnail } from '../../CollectionThumbnail/CollectionThumbnail';
import React from 'react';
import { ContentfulImage } from '../../ContentfulImage/ContentfulImage';
import { AssetTransformer } from './AssetTransformer';

export abstract class CollectionTransformer {
  static toProductSourceProps = (entry: ContentfulCollection): Partial<UseProductsProps> => {
    return {
      productSkus: [...(entry.fields?.heroProducts || []), ...(entry.fields?.cataloguedSkus || [])],
      algoliaQuery: entry.fields
        ? entry.fields.algoliaFilterQuery || `"category_hierarchy": "${entry.fields.title}"`
        : undefined,
      ...(entry.fields?.defaultSortMethod && { options: { sortByMethod: entry.fields.defaultSortMethod } }),
    };
  };

  static toHref = (entry: ContentfulCollection | undefined): string => {
    const removeHandleId = (handle: string) => handle.split('_')[0];

    if (!entry?.fields?.handle) return '';
    if (!entry?.fields?.nestedCollection || !entry?.fields?.ancestorCollections?.[0]?.fields?.handle) {
      return `/collections/${removeHandleId(entry?.fields?.handle)}`;
    }
    return `/collections/${removeHandleId(entry?.fields?.ancestorCollections?.[0]?.fields?.handle)}/${removeHandleId(
      entry?.fields?.handle
    )}`;
  };

  static toBrowseNavItems = (entry?: ContentfulCollection): Array<ContentfulCollection | ContentfulNavItem> => {
    const isValidNavItem = (item: BrowseNavItem): boolean =>
      Boolean((isCollection(item) && item.fields?.title) || (isNavItem(item) && item.fields?.name));

    const items: Array<ContentfulCollection | ContentfulNavItem> = [];
    entry?.fields?.browseNav?.forEach(item => {
      if (isValidNavItem(item)) {
        items.push(item);
      }
      if (isBrowseGroup(item)) {
        items.push(...(item.fields?.browseItems.filter(isValidNavItem) ?? []));
      }
    });

    return [...new Map(items.map(item => [item.fields?.handle, item])).values()];
  };

  static toNavChipsProps = (entry?: ContentfulCollection): Array<{ label: string; href: string }> | undefined => {
    if (!entry) return undefined;
    return this.toBrowseNavItems(entry)
      ?.map(item => {
        if (isCollection(item))
          return {
            label: item?.fields?.title ?? '',
            href: this.toHref(item),
          };
        return { label: item.fields?.name ?? '', href: item.fields?.url ?? '' };
      })
      ?.filter(item => item.href && item.label);
  };

  static toThumbnailImagesProps = (entry?: ContentfulCollection): Array<ThumbnailImageProps> | undefined => {
    if (!entry) return undefined;
    return this.toBrowseNavItems(entry)
      ?.map(item => {
        if (isCollection(item)) {
          return {
            title: item.fields?.title ?? '',
            href: this.toHref(item),
            image: <CollectionThumbnail collection={item} sizePx={{ xs: 100, md: 120 }} />,
          };
        }
        return {
          title: item.fields?.name ?? '',
          href: item.fields?.url ?? '',
          image: item.fields?.browseNavImage?.fields ? (
            <ContentfulImage {...AssetTransformer.toContentfulImageProps(item.fields.browseNavImage)} sizes="120px" />
          ) : (
            <CollectionThumbnail sizePx={{ xs: 100, md: 120 }} handle={item.fields?.handle} />
          ),
        };
      })
      ?.filter(item => item.href && item.title);
  };

  static toUseThumbnailProductProps = (
    entry?: ContentfulCollection
  ): Omit<UseProductsProps, 'productClient'> | undefined => {
    if (!entry?.fields) return undefined;
    const template = entry.fields.template?.toLowerCase() as Lowercase<
      Exclude<ContentfulCollection['fields'], undefined>['template']
    >;

    if (['catalogue', 'enhanced', 'merchstudio'].includes(template)) {
      const { heroProducts = [], cataloguedSkus = [] } = entry.fields;
      const { algoliaQuery } = this.toProductSourceProps(entry);
      const productSkus =
        template === 'enhanced' || template === 'merchstudio'
          ? heroProducts.concat(cataloguedSkus)
          : cataloguedSkus.concat(heroProducts);
      return productSkus.length || algoliaQuery
        ? { productSkus, algoliaQuery, algoliaSearchOptions: { hitsPerPage: 1 } }
        : undefined;
    } else if (template === 'curated') {
      let data: UseProductsProps | undefined = undefined;
      entry?.fields?.curatedCollections?.some(col =>
        Boolean((data = CollectionTransformer.toUseThumbnailProductProps(col)))
      );
      return data;
    }
    return undefined;
  };
}

type BrowseNavItem = Exclude<Exclude<ContentfulCollection['fields'], undefined>['browseNav'], undefined>[number];

const isCollection = (item: BrowseNavItem): item is ContentfulCollection =>
  item?.sys?.contentType?.sys?.id === 'smartCollection';

const isNavItem = (item: BrowseNavItem): item is ContentfulNavItem => item?.sys?.contentType?.sys?.id === 'navItem';

const isBrowseGroup = (item: BrowseNavItem): item is ContentfulBrowseGroup =>
  item?.sys?.contentType?.sys?.id === 'browseGroup';
